class PaginationLoader extends HTMLElement {
  constructor() {
    super();
    this.skeletonTopLoader = document.querySelector('[data-skeleton-pagination-top]');
    this.skeletonBottomLoader = document.querySelector('[data-skeleton-pagination-bottom]');
    this.loadMoreButton = this.querySelector('.js-load-more');
    this.totalPages = Number.parseInt(this.querySelector('[data-total-pages]')?.value || '0', 10);
    this.inputNextUrl = this.querySelector('[data-next-url]');
    this.abortController = null;

    if (this.loadMoreButton) this.loadMoreButton.addEventListener('click', this.onLoadMoreClick.bind(this));

    if (window.location.search && this.dataset.disableLoadPages !== 'true') this.loadPages();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }

  async fetchMarkup(url) {
    const response = await fetch(url, { signal: this.abortController?.signal });
    if (!response.ok) throw new Error(`Pagination request failed with ${response.status}`);
    return response.text();
  }

  async loadContent(nextUrl) {
    if (!nextUrl || !this.loadMoreButton) return;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    this.loadMoreButton.setAttribute('disabled', 'true');
    this.loadMoreButton.classList.add('btn--loading');
    this.handleLoadingState(true, true);

    try {
      const responseText = await this.fetchMarkup(nextUrl);
      const id = 'data-pagination';
      const htmlElm = new DOMParser().parseFromString(responseText, 'text/html');
      const sectionInnerHTML = htmlElm.getElementById(id);
      const productsElm = document.getElementById(id);
      const totalPagesInput = htmlElm.querySelector('[data-total-pages]');
      const currentPageInput = htmlElm.querySelector('[data-current-page]');
      const nextPageInput = htmlElm.querySelector('[data-next-url]');

      if (!sectionInnerHTML || !productsElm || !currentPageInput) {
        throw new Error('Pagination response did not contain the expected product grid');
      }

      const totalPages = Number.parseInt(totalPagesInput?.value || String(this.totalPages), 10);
      const currentPage = Number.parseInt(currentPageInput.value || '1', 10);
      const nextPageUrl = nextPageInput?.value || '';

      const localTotal = this.querySelector('[data-total-pages]');
      const localCurrent = this.querySelector('[data-current-page]');
      const localNext = this.querySelector('[data-next-url]');
      if (localTotal && Number.isFinite(totalPages)) localTotal.value = String(totalPages);
      if (localCurrent && Number.isFinite(currentPage)) localCurrent.value = String(currentPage);
      if (localNext) localNext.value = nextPageUrl;

      document.querySelectorAll('input[name="page"]').forEach((element) => {
        if (Number.isFinite(currentPage)) element.value = String(currentPage);
      });

      let focusElem = null;
      sectionInnerHTML.querySelectorAll('.data-pagination__item').forEach((element, index) => {
        if (index === 0) focusElem = element.querySelector('.grid-view-item__link, a[href]');
        productsElm.appendChild(element);
      });

      // Do not mutate Shopify CDN image URLs. Cache-busting every appended product image
      // wastes bandwidth and can create malformed URLs.
      if (!nextPageUrl || (Number.isFinite(totalPages) && currentPage >= totalPages)) {
        this.loadMoreButton.classList.add('hide');
      }

      if (focusElem) focusElem.focus({ preventScroll: true });
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.loadMoreButton.removeAttribute('disabled');
      this.loadMoreButton.classList.remove('btn--loading');
      this.handleLoadingState(false, true);
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const urlParams = event.state ? event.state.urlParams : PaginationLoader.urlParamsInitial;
      if (urlParams === PaginationLoader.urlParamsPrev) return;
      const loader = document.querySelector('pagination-loader');
      if (loader) loader.renderPage(urlParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  renderPage(urlParams, event, updateURLHash = true) {
    if (!urlParams) return;
    if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = urlParams;

    const url = `${window.location.pathname}?${urlParams}`;
    this.loadContent(url);

    if (updateURLHash) PaginationLoader.updateURLHash(urlParams);
  }

  static updateURLHash(urlParams) {
    PaginationLoader.urlParamsPrev = urlParams;
    history.pushState({ urlParams }, '', `${urlParams ? `?${urlParams}` : window.location.pathname}`);
  }

  async loadPages() {
    const params = getAllUrlParams(window.location.href);
    const page = params.page ? Number.parseInt(params.page, 10) : null;
    if (!page || page <= 1) return;

    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const pagesArray = Array.from({ length: page - 1 }, (_, index) => index + 1);
    const id = 'data-pagination';
    const productsElm = document.getElementById(id);
    if (!productsElm) return;

    this.handleLoadingState(true);
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const texts = await Promise.all(
        pagesArray.map((pageNumber) => {
          const fetchUrl = currentUrl.match(/page=\d+/)
            ? currentUrl.replace(/page=\d+/, `page=${pageNumber}`)
            : `${currentUrl}${currentUrl.includes('?') ? '&' : '?'}page=${pageNumber}`;
          return this.fetchMarkup(fetchUrl);
        })
      );

      const fragment = document.createDocumentFragment();
      texts.forEach((responseText) => {
        const htmlElm = new DOMParser().parseFromString(responseText, 'text/html');
        const sectionInnerHTML = htmlElm.getElementById(id);
        if (!sectionInnerHTML) return;
        sectionInnerHTML.querySelectorAll('.data-pagination__item').forEach((element) => fragment.appendChild(element));
      });
      productsElm.prepend(fragment);

      if (this.totalPages && page >= this.totalPages && this.loadMoreButton) this.loadMoreButton.classList.add('hide');
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      this.handleLoadingState(false);
    }
  }

  handleLoadingState(isLoading, loadingNextPage = false) {
    if (isLoading) {
      const loader = loadingNextPage ? this.skeletonBottomLoader : this.skeletonTopLoader;
      if (loader) loader.classList.remove('hide');
      return;
    }

    if (this.skeletonTopLoader) this.skeletonTopLoader.classList.add('hide');
    if (this.skeletonBottomLoader) this.skeletonBottomLoader.classList.add('hide');
  }

  onLoadMoreClick(event) {
    event.preventDefault();
    const nextUrl = this.inputNextUrl?.value || '';
    if (!nextUrl) {
      if (this.loadMoreButton) this.loadMoreButton.classList.add('hide');
      return;
    }

    let nextUrlParams = '';
    try {
      const parsed = new URL(nextUrl, window.location.origin);
      nextUrlParams = parsed.search.slice(1);
    } catch (error) {
      console.error(error);
      return;
    }

    this.renderPage(nextUrlParams);
  }
}

PaginationLoader.paginationData = [];
PaginationLoader.urlParamsInitial = window.location.search.slice(1);
PaginationLoader.urlParamsPrev = window.location.search.slice(1);
if (!customElements.get('pagination-loader')) {
  customElements.define('pagination-loader', PaginationLoader);
}
